// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 28-it-dashboard-ui.md
import { z } from 'zod';
import { FleetEntitlementResponseSchema } from './fleet-entitlement.js';
const Timestamp = z.string().datetime({ offset: true });
const Window = z.object({ start: Timestamp, end: Timestamp });
const Rate = z.number().finite().nonnegative().max(1_000);
/**
 * The card the fleet forecast prices this install with (docs/27 §API). A rate
 * is `null` when the install's terms do not charge that dimension.
 */
export const FleetRatesSchema = z.object({
    source: z.enum(['installation', 'grandfathered', 'marketplace_listing', 'policy']),
    billing_model: z.enum(['usage', 'design_partner']),
    monthly_fee_usd: z.number().finite().nonnegative().max(10_000_000),
    usd_per_deployment: Rate.nullable(),
    usd_per_mau_per_app: Rate.nullable(),
    usd_per_app_hour: Rate.nullable(),
    usd_per_live_app: Rate.nullable(),
    monthly_license_usd: z.number().finite().nonnegative().max(10_000_000).nullable(),
    as_of: Timestamp.nullable(),
});
/**
 * The fleet monitor's `GET /api/v1/billing-summary` answer (docs/27 §API), as
 * the control plane accepts it. Fields Shift adds later pass through unread;
 * a field this build reads that is missing or malformed rejects the whole
 * summary rather than rendering half of it.
 */
export const FleetBillingSummarySchema = z.object({
    installation_id: z.string(),
    // The install's tolerant reading: a state or reason Shift adds later degrades
    // to `unknown` / unrecognized instead of rejecting the whole summary.
    entitlement: FleetEntitlementResponseSchema,
    evaluated_at: Timestamp,
    account: z
        .object({
        standing: z.object({
            standing: z.enum(['current', 'grace', 'lapsed', 'unknown']),
            reason: z.string().max(64),
            effective_at: Timestamp.nullable(),
        }),
        terms: z
            .object({
            label: z.string().min(1).max(200),
            summary: z.string().max(500).nullable(),
            pricing_policy_version: z.number().int().positive(),
            effective_from: Timestamp,
        })
            .nullable(),
        last_settlement: z
            .object({
            window: Window,
            denomination: z.enum(['monetary', 'credits']),
            net_minor: z.number().int(),
            currency: z.literal('usd').nullable(),
            collection_state: z.enum([
                'priced',
                'collection_pending',
                'collected',
                'collection_failed',
                'manual',
            ]),
        })
            .nullable(),
        balance: z
            .object({
            amount: z.number().finite(),
            unit: z.string().min(1).max(32),
            as_of: Timestamp,
            expires_at: Timestamp.nullable(),
        })
            .nullable(),
        capability: z.object({ hosted_management: z.boolean() }),
        payment_method_on_file: z.boolean().nullable(),
    })
        .nullable(),
    usage: z
        .array(z.object({
        meter_key: z.string().min(1).max(64),
        label: z.string().min(1).max(100),
        quantity: z.number().finite().nonnegative(),
        unit: z.string().min(1).max(32),
        window: Window,
        complete: z.boolean(),
    }))
        .max(50),
    /** Absent from a monitor that predates it; a malformed card reads as absent rather than costing the Billing page its summary. */
    rates: FleetRatesSchema.optional().catch(undefined),
});
/** `GET /api/settings/billing`: the summary, or why the page cannot show one. */
export const BillingSettingsSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('available'), summary: FleetBillingSummarySchema }),
    /** The install has not enrolled with Shift yet, so it has no billing identity to ask about. */
    z.object({ status: z.literal('not_enrolled') }),
    /** Bought through a cloud marketplace: that cloud bills it, and Shift holds no account for it. */
    z.object({ status: z.literal('marketplace'), cloud: z.enum(['azure', 'aws']) }),
    z.object({
        status: z.literal('unavailable'),
        reason: z.enum(['unreachable', 'refused', 'malformed']),
    }),
]);
/**
 * `GET /api/install/payment`: what the install wizard's Payment method step may
 * know before anyone signs in, and nothing more (docs/28 §/install).
 */
export const InstallPaymentStatusSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('available'),
        payment_method_on_file: z.boolean().nullable(),
        hosted_management: z.boolean(),
    }),
    /** Enrolled, but Shift has no billing account for the install yet. */
    z.object({ status: z.literal('no_account') }),
    /** Not enrolled with Shift yet; the step can be done later in Settings → Billing. */
    z.object({ status: z.literal('not_enrolled') }),
    z.object({ status: z.literal('unavailable') }),
]);
export const BILLING_SESSION_PURPOSES = ['setup', 'manage'];
/**
 * A provider-hosted payment page the browser may be sent to. Only `https` on a
 * provider host the install trusts; anything else is never followed.
 */
export const BILLING_SESSION_HOST_SUFFIXES = ['.stripe.com'];
export const BillingSessionSchema = z.object({
    url: z.string().refine((value) => {
        if (!URL.canParse(value))
            return false;
        const url = new URL(value);
        return (url.protocol === 'https:' &&
            url.username === '' &&
            url.password === '' &&
            BILLING_SESSION_HOST_SUFFIXES.some((suffix) => url.hostname.endsWith(suffix)));
    }, 'session url must be https on a payment-provider host'),
    expires_at: Timestamp,
    purpose: z.enum(BILLING_SESSION_PURPOSES),
});
//# sourceMappingURL=fleet-billing.js.map